
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SOCIAL_LINKS, CONTACT_INFO, getCompanyLinks } from '../../../utils/constants';
import { FooterSection } from './FooterSection';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FiX } from 'react-icons/fi';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-gray-300" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
            {/* Company Info */}
            <FooterSection>
              <div className="flex flex-col gap-2">
                <h2 className="text-xl sm:text-2xl font-bold text-white">{t('footer.companyName')}</h2>
                <span className="text-xs sm:text-sm text-gray-400">{t('footer.companySubtitle')}</span>
              </div>
              <p className="text-xs sm:text-sm leading-relaxed text-gray-400">
                {t('footer.companyDescription')}
              </p>
              <nav className="flex gap-3 sm:gap-4" aria-label={t('footer.socialLinks')}>
                {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-secondary hover:scale-110 active:scale-95 transition-all touch-manipulation"
                    aria-label={t('footer.visitSocial', { 0: label })}
                  >
                    <Icon size={20} aria-hidden="true" />
                  </a>
                ))}
              </nav>
            </FooterSection>

            {/* Contact */}
            <FooterSection title={t('footer.contactTitle')}>
              <address className="not-italic flex flex-col gap-3">
                {CONTACT_INFO.map(({ icon: Icon, content, href, type }, index) => (
                  <div key={index} className="flex items-start gap-3">
                    {type === 'link' ? (
                      <a
                        href={href}
                        className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 hover:bg-secondary hover:text-white transition-all"
                        aria-label={content}
                      >
                        <Icon size={16} />
                      </a>
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        <Icon size={16} />
                      </span>
                    )}
                    {type === 'link' ? (
                      <a href={href} className="hover:text-secondary transition-colors text-sm break-all pt-1.5">
                        {content}
                      </a>
                    ) : (
                      <span className="text-sm leading-relaxed pt-1.5">{content}</span>
                    )}
                  </div>
                ))}
              </address>
            </FooterSection>

            {/* Company Links */}
            <FooterSection title={t('footer.companyInfoTitle')}>
              <nav aria-label={t('footer.footerNav')}>
                <ul className="flex flex-col gap-2.5">
                  {getCompanyLinks(t).map(({ path, label }) => (
                    <li key={path}>
                      <Link
                        to={path}
                        className="hover:text-secondary transition-colors inline-flex items-center gap-2 text-sm touch-manipulation"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" aria-hidden="true" />
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </FooterSection>
          </div>

          {/* Bottom Bar */}
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-400">
            <p className="text-center md:text-left">{t('footer.copyright', { 0: currentYear })}</p>
            <nav className="flex flex-wrap items-center justify-center gap-3 sm:gap-4" aria-label={t('footer.legalLinks')}>
              <Link to="/privacy" className="hover:text-secondary transition-colors touch-manipulation">
                {t('footer.privacy')}
              </Link>
              <span aria-hidden="true">|</span>
              <Link to="/terms" className="hover:text-secondary transition-colors touch-manipulation">
                {t('footer.terms')}
              </Link>
            </nav>
            <button
              onClick={() => setIsModalOpen(true)}
              className="hover:scale-110 active:scale-95 transition-transform cursor-pointer touch-manipulation"
              aria-label={t('footer.viewCertificate')}
            >
              <img
                src="/img/iata.png"
                alt="IATA Accredited"
                className="h-10 sm:h-12 w-auto"
              />
            </button>
          </div>
        </div>
      </footer>

      {/* Certificate Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[85vh] sm:h-[90vh] relative flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 sm:p-4 border-b flex-shrink-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t('footer.certificateTitle')}</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 sm:p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
                aria-label={t('footer.close')}
              >
                <FiX size={24} className="text-gray-600" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src="/certificate.pdf"
                className="w-full h-full"
                title="IATA Certificate"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
